import React from "react";
import PropTypes from "prop-types";
import useViewportChangeTick from "../../../hooks/useViewportChangeTick";
import calculateFloatingPosition from "../../../utils/floatingPosition";

function MentionDropdown({
  anchor = null,
  items = [],
  activeIndex = 0,
  onSelect,
  onHighlight,
  type = "channel",
  query = "",
}) {
  const dropdownRef = React.useRef(null);
  const [coords, setCoords] = React.useState({ top: anchor?.top ?? 0, left: anchor?.left ?? 0 });

  const resolveScrollContainers = React.useCallback(() => {
    const container = dropdownRef.current?.offsetParent || null;
    return container ? [container] : [];
  }, []);

  const viewportTick = useViewportChangeTick(resolveScrollContainers, []);

  // Recompute coordinates to keep the dropdown fully visible within the viewport.
  React.useLayoutEffect(() => {
    const el = dropdownRef.current;
    const container = el?.offsetParent || null;
    const anchorRect = {
      top: anchor?.top ?? 0,
      left: anchor?.left ?? 0,
      width: anchor?.width ?? 0,
      height: anchor?.height ?? 0,
    };
    if (!el || !container) {
      setCoords({ top: anchorRect.top, left: anchorRect.left });
      return;
    }

    const nextCoords = calculateFloatingPosition({
      container,
      dropdown: el,
      anchor: anchorRect,
      gap: 8,
      offset: { x: 0, y: 6 },
      align: "start",
      flip: true,
    });

    setCoords(nextCoords);
  }, [
    anchor?.top,
    anchor?.left,
    anchor?.width,
    anchor?.height,
    items?.length,
    type,
    query,
    viewportTick,
  ]);

  if (!items?.length) {
    return (
      <div
        className="channel-mention-dropdown"
        style={{ top: coords.top, left: coords.left }}
        role="listbox"
        aria-label="No mention suggestions"
        ref={dropdownRef}
      >
        <div className="channel-mention-empty">
          {query ? `No matches for "${query}"` : "No suggestions"}
        </div>
      </div>
    );
  }

  const prefix = type === "channel" ? "#" : "@";

  return (
    <div
      className="channel-mention-dropdown"
      style={{ top: coords.top, left: coords.left }}
      role="listbox"
      aria-label={type === "channel" ? "Channel suggestions" : "User suggestions"}
      ref={dropdownRef}
    >
      {items.map((item, index) => {
        const isActive = index === activeIndex;
        return (
          <button
            key={item.id || `${item.value}-${index}`}
            type="button"
            className={`channel-mention-item${isActive ? " is-active" : ""}`}
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => onHighlight?.(index)}
            onFocus={() => onHighlight?.(index)}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect?.(item);
            }}
          >
            <span className="channel-mention-item__hash">{prefix}</span>
            <div>
              <div>{item.value || item.label}</div>
              {item.description && (
                <div className="channel-mention-item__meta">{item.description}</div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

MentionDropdown.propTypes = {
  anchor: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  activeIndex: PropTypes.number,
  onSelect: PropTypes.func,
  onHighlight: PropTypes.func,
  type: PropTypes.oneOf(["channel", "user"]),
  query: PropTypes.string,
};

export default MentionDropdown;
